import {
	Banknote,
	Calendar,
	Coffee,
	type LucideIcon,
	PiggyBank,
	Plane,
	Shield,
	TrendingUp,
	Zap,
} from "lucide-react";
import earlyFreedomImg from "shared/assets/inspiration/ins-022-early-freedom.jpg";
import type { ObjPathProxy } from "ts-object-path";
import { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import type { MyPlannResourceDictionary } from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type { InspirationTimelineStep } from "web_myplann/Inspiration/Components/InspirationTimeline";
import type { EarlyFreedomAnchorKey } from "web_myplann/Inspiration/EarlyFreedom/Types/EarlyFreedomTypes";

export const EARLY_FREEDOM_IMAGE = earlyFreedomImg;

export const EARLY_FREEDOM_GOAL_CATEGORY = GoalCategoryTypeEnum.Rent;
export const EARLY_FREEDOM_SAFE_WITHDRAWAL_YEARS = 25;
export const EARLY_FREEDOM_MAX_YEARS = 60;

export const EARLY_FREEDOM_TARGET_MIN = 10_000;
export const EARLY_FREEDOM_TARGET_MAX = 200_000;
export const EARLY_FREEDOM_TARGET_STEP = 1_000;
export const EARLY_FREEDOM_TARGET_FALLBACK = 45_000;

export const EARLY_FREEDOM_RENT_MIN = 0;
export const EARLY_FREEDOM_RENT_MAX = 200_000;
export const EARLY_FREEDOM_RENT_STEP = 500;

export const EARLY_FREEDOM_SAVING_MIN = 0;
export const EARLY_FREEDOM_SAVING_MAX = 200_000;
export const EARLY_FREEDOM_SAVING_STEP = 1_000;
export const EARLY_FREEDOM_SAVING_FALLBACK = 5_000;

export const EARLY_FREEDOM_ANCHOR_MODEST = 25_000;
export const EARLY_FREEDOM_ANCHOR_COMFORT = 45_000;
export const EARLY_FREEDOM_ANCHOR_LUXURY = 80_000;

const AnchorResources = Resources.Inspiration.EarlyFreedom.Anchors;
const PhaseResources = Resources.Inspiration.EarlyFreedom.Phases;
const ReadyStepResources = Resources.Inspiration.EarlyFreedom.ReadySteps;

export type EarlyFreedomAnchorOption = {
	key: EarlyFreedomAnchorKey;
	icon: LucideIcon;
	amount: number;
	copy: ObjPathProxy<
		MyPlannResourceDictionary,
		{ label: string; description: string }
	>;
};

export const EARLY_FREEDOM_EXAMPLE_RENT = 15_000;

export const EARLY_FREEDOM_LIFESTYLE_ANCHORS: EarlyFreedomAnchorOption[] = [
	{
		key: "modest",
		icon: Coffee,
		amount: EARLY_FREEDOM_ANCHOR_MODEST,
		copy: AnchorResources.modest,
	},
	{
		key: "comfort",
		icon: Banknote,
		amount: EARLY_FREEDOM_ANCHOR_COMFORT,
		copy: AnchorResources.comfort,
	},
	{
		key: "luxury",
		icon: Plane,
		amount: EARLY_FREEDOM_ANCHOR_LUXURY,
		copy: AnchorResources.luxury,
	},
];

export const EARLY_FREEDOM_PHASES: InspirationTimelineStep[] = [
	{ icon: PiggyBank, step: PhaseResources.diversify },
	{ icon: TrendingUp, step: PhaseResources.build },
	{ icon: Shield, step: PhaseResources.approaching },
	{ icon: Zap, step: PhaseResources.living },
];

export type EarlyFreedomReadyStep = {
	icon: LucideIcon;
	title: ObjPathProxy<MyPlannResourceDictionary, string>;
	subtitle: ObjPathProxy<MyPlannResourceDictionary, string>;
};

export const EARLY_FREEDOM_READY_STEPS: EarlyFreedomReadyStep[] = [
	{
		icon: Calendar,
		title: ReadyStepResources.drawdown.title,
		subtitle: ReadyStepResources.drawdown.subtitle,
	},
	{
		icon: Shield,
		title: ReadyStepResources.portfolio.title,
		subtitle: ReadyStepResources.portfolio.subtitle,
	},
];
